import React, { forwardRef, useImperativeHandle } from 'react'
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_KEY, MY_PGA_COM_HOST } from 'env'
import qs from 'query-string'
import { useLocation } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { LinearProgress } from '@mui/material'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import { gtmEvent } from 'lib/gtm'
import AlertWithDivider from '../components/AlertWithDivider'
import { clearGuestBooking } from '../guest-booking'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import { useAuth } from 'lib/auth'
import { useSetMarketingOptIn } from 'components/marketing-opt-in/hooks'

const stripePromise = loadStripe(STRIPE_KEY)

const DEFAULT_APPEARANCE = { theme: 'stripe' }

// Inner component that uses Stripe hooks - must be inside Elements provider with clientSecret
const PaymentElementForm = forwardRef(
  (
    {
      clientSecret,
      coachSlug,
      studentId,
      marketingOptInChecked,
      eventObject,
      onSubmitError,
    },
    ref,
  ) => {
    const stripe = useStripe()
    const elements = useElements()
    const { enqueueSnackbar } = useSnackbar()
    const snackbarError = useSnackbarError(enqueueSnackbar)
    // The redesign shows errors inline; the legacy path keeps its snackbar.
    const reportError = message =>
      onSubmitError ? onSubmitError(message) : snackbarError(message)
    const location = useLocation()
    const { setMarketingOptIn } = useSetMarketingOptIn()

    const { lessonTypeId } = qs.parse(location.search)

    const paymentElementOptions = {
      layout: 'tabs',
    }

    const validatePayment = async () => {
      if (!stripe || !elements || !clientSecret) {
        onSubmitError?.(
          'Secure checkout is still loading. Try again in a moment.',
        )
        return false
      }

      const { error: submitError } = await elements.submit()
      if (submitError) {
        reportError(submitError.message)
        return false
      }

      return true
    }

    const submitPayment = async () => {
      if (!stripe || !elements || !clientSecret) {
        return
      }

      const billingDetails = {
        // We can add more billing details here as needed
      }

      const { error: submitError } = await elements.submit()

      if (submitError) {
        reportError(submitError.message)
        return
      }

      if (marketingOptInChecked) {
        gtmEvent({
          event: 'marketing-opt-in',
          source: 'private_booking',
        })
        setMarketingOptIn(marketingOptInChecked)
      }

      gtmEvent({
        ...eventObject,
        event: 'complete-private-session-booking',
      })

      // Also fire the legacy event
      gtmEvent({
        event: 'formSubmit',
        formCategory: 'book-session',
        formAction: 'clicked-book-session-only-button',
      })

      const studentSegment = studentId ? `/student/${studentId}` : ''
      const returnUrl = `${MY_PGA_COM_HOST}/coach/${coachSlug}${studentSegment}/purchase/private-lesson/${lessonTypeId}`

      clearGuestBooking()

      const { error } = await stripe.confirmPayment({
        elements,
        redirect: 'always',
        confirmParams: {
          expand: ['payment_method'],
          return_url: returnUrl,
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        clientSecret: clientSecret,
      })

      if (error) {
        reportError(error.message)
      }
    }

    useImperativeHandle(ref, () => ({
      submitPayment,
      validatePayment,
    }))

    return (
      <PaymentElement id="payment-element" options={paymentElementOptions} />
    )
  },
)

// Outer component that provides Elements context with clientSecret
const StripePaymentElementsForm = forwardRef(
  (
    {
      clientSecret,
      secretLoading,
      coachSlug,
      studentId,
      marketingOptInChecked,
      eventObject,
      paymentRequired,
      setPaymentRequired,
      lessonType,
      // The redesigned review step renders its own heading, skip payment
      // switch and security note, and styles the Payment Element itself.
      showHeading = true,
      appearance = DEFAULT_APPEARANCE,
      onSubmitError,
    },
    ref,
  ) => {
    const { user } = useAuth()
    const canSkipPayment = user?.id === lessonType?.coach?.id

    if (!lessonType.acceptsOnlinePayments) {
      return null
    }

    return (
      <>
        {showHeading && (
          <Box mb={3} mt={4} pt={2}>
            <Typography variant="h4">
              <Box display="flex" justifyContent="space-between">
                {clientSecret ? 'Enter payment details' : ''}
                {canSkipPayment && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!paymentRequired}
                        onChange={() => setPaymentRequired(!paymentRequired)}
                      />
                    }
                    label="Skip payment"
                  />
                )}
              </Box>
            </Typography>
          </Box>
        )}
        {secretLoading && <LinearProgress />}
        {clientSecret && (
          <Elements
            key={clientSecret}
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance,
            }}
          >
            {showHeading && (
              <Box mb={4}>
                <AlertWithDivider text="Your credit card information is not being stored in our system." />
              </Box>
            )}
            <PaymentElementForm
              ref={ref}
              clientSecret={clientSecret}
              coachSlug={coachSlug}
              studentId={studentId}
              marketingOptInChecked={marketingOptInChecked}
              eventObject={eventObject}
              onSubmitError={onSubmitError}
            />
          </Elements>
        )}
      </>
    )
  },
)

export default StripePaymentElementsForm
